import { ReportsUtils } from './reportsUtils'
import Alert from './alert'
import ReportUtils from './reportUtils'
import ObjCheck from './objCheck'
import { MetadataField, ReportField, SrcFieldValue } from '../constnats/reportConstants'
import { normalizeValue } from './normalize'
import DuplicateReportError from '../customErrors/duplicateReportsError'
import Assert from './asserts';

const METADATA_PROPS_FOR_REVIEW = [
    MetadataField.CORRECTION,
    MetadataField.REPORTED_AS_LABEL,
    MetadataField.MANUALLY_CALCED,
    MetadataField.CALCS_DISABLED,
    MetadataField.EXTENDED_SEARCH,
    MetadataField.SRC
]

const FIELD_SOURCE_REPORT_EQUIVALENT_VALUES = ["", SrcFieldValue.REPORT, null, undefined]

const BOOLEAN_METADATA_PROPS = [MetadataField.MANUALLY_CALCED, MetadataField.CALCS_DISABLED, MetadataField.EXTENDED_SEARCH]

const DESCRIPTOR_IDS_TO_IGNORE_METADATA_REVIEW_FOR = [ReportField.TYPE, ReportField.SRC, ReportField.REPORT_TYPE]

class ReportDiffUtil {

    /**
     * @param {[]} editReports 
     * @param {[]} reviewReports 
     * @param {[]} descriptorPairs
     * @param {boolean} ignoreNonCoreData
     * @param {boolean} ignoreReportDuplications
     * @returns {Diff[]} not-null diffs
     */
    static reportsDiff(editReports, reviewReports, descriptorPairs,
        ignoreNonCoreData = false, ignoreReportDuplications = false, ignoreExtendedSearchEnablement = false) {
        editReports = ReportsUtils.sort(editReports)
        const diffs = []
        const reportPairs = this._mapReports(editReports, reviewReports, ignoreReportDuplications)

        for (const reportPair of reportPairs) {
            const editReport = reportPair.editReport && ReportUtils.flattenDynamicFields(reportPair.editReport)
            const reviewReport = reportPair.reviewReport && ReportUtils.flattenDynamicFields(reportPair.reviewReport)
            const reportInfo = editReport ? editReport : reviewReport
            const diffFactory = new DiffFactory({
                year: reportInfo[ReportField.YEAR],
                period:reportInfo[ReportField.PERIOD],
                version: reportInfo[ReportField.TYPE],
                format: reportInfo[ReportField.REPORT_TYPE],
                editReportId: editReport?.[ReportField.ID] ?? null,
                reviewReportId: reviewReport?.[ReportField.ID] ?? null
            })

            if (!editReport) {
                diffs.push(diffFactory.create("N/A", "N/A", "Not Found", "Found"))
                continue
            } else if (!reviewReport) {
                diffs.push(diffFactory.create("N/A", "N/A", "Found", "Not Found"))
                continue
            }

            diffs.push(...this._reportPairDiff(editReport, reviewReport, descriptorPairs, ignoreNonCoreData,
                ignoreExtendedSearchEnablement, diffFactory))
        }

        return diffs
    }

    /**
     * @static
     * @param {Diff[]} reportDiffs
     * @param {boolean} [desc=false]
     * @memberof ReportDiffUtil
     * @returns {Diff[]}
     */
    static sortReportDiffs (reportDiffs, desc=false) {
        reportDiffs.sort((a, b) => ReportsUtils.compare(a.reportInfo, b.reportInfo, desc));
        return reportDiffs
    }

    /**
     * @returns {[]} not-null report pairs
     */
    static _mapReports(editReports, reviewReports, ignoreReportDuplications) {
        const editReportsMap = this._toMap(editReports, ignoreReportDuplications)
        const reviewReportsMap = this._toMap(reviewReports, ignoreReportDuplications)

        const pairedReviewReports = []
        const reportPairs = []

        for (const name of editReportsMap.keys()) {
            reportPairs.push(new ReportPair(editReportsMap.get(name), reviewReportsMap.get(name)))
            pairedReviewReports.push(name)
        }

        reviewReports.filter(r => !pairedReviewReports.includes(this._getGetName(r)))
            .forEach(r => reportPairs.push(new ReportPair(null, r)));


        return reportPairs;
    }

    static _toMap(reports, ignoreReportDuplications) {
        const reportsMap = new Map()

        for (const report of reports) {
            const reportName = this._getGetName(report)
            if (reportsMap.has(reportName)) {
                Alert.error(`"At least one of the branches contains more than two report for:"${reportName}`, "", false)
                if (!ignoreReportDuplications) {
                    throw new DuplicateReportError()
                }
            } else {
                reportsMap.set(reportName, report)
            }
        }

        return reportsMap
    }


    static _reportPairDiff(editReport, reviewReport, descriptorPairs, ignoreNonCoreData, ignoreExtendedSearchEnablement,diffFactory) {
        const diffs = []


        for (const descriptorPair of descriptorPairs) {
            const editBranchDesc = descriptorPair.editBranchDesc
            const reviewBranchDesc = descriptorPair.reviewBranchDesc

            const label = reviewBranchDesc !== null ? reviewBranchDesc.label : editBranchDesc.label

            //TMP fast implematation for core data checks
            if (editBranchDesc == null || reviewBranchDesc == null) {
                if (editBranchDesc === null) {
                    const prop = reviewBranchDesc.id
                    const val = reviewReport[prop]
                    if (!ObjCheck.isNullUndefinedEmptyOrDash(val)) {
                        // Only the presented side is addressable; the other has no descriptor at all.
                        diffs.push(diffFactory.create(
                            label, "ALL", "No Data", "Presented (val:" + val + ")",
                            Diff.ERROR, undefined, prop))
                    }


                } else {
                    const prop = editBranchDesc.id
                    const val = editReport[prop]
                    if (!ObjCheck.isNullUndefinedEmptyOrDash(val)) {
                        diffs.push(diffFactory.create(
                            label, "ALL", "Presented (val:" + val + ")", "No Data",
                            Diff.ERROR, prop, undefined))
                    }
                }

                continue
            }

            const editProp = editBranchDesc.id
            const reviewProp = reviewBranchDesc.id

            const editFieldVal = normalizeValue(editReport[editProp])
            const reviewFieldVal = normalizeValue(reviewReport[reviewProp])

            if (ObjCheck.isNullUndefinedEmptyOrDash(editFieldVal) &&
                ObjCheck.isNullUndefinedEmptyOrDash(reviewFieldVal)) {
                if (ignoreNonCoreData !== true) {
                    diffs.push(...MetadataDiffExtractor.extract(label, editReport, reviewReport, editProp, reviewProp, ignoreExtendedSearchEnablement, diffFactory, true))
                }
            } else {
                if (ObjCheck.isNullOrUndefined(editFieldVal)) {
                    // Only the presented side is addressable — "No Data" has no source to point at.
                    diffs.push(diffFactory.create(label, "ALL", "No Data", "Presented (val:" + reviewFieldVal + ")",
                        Diff.ERROR, undefined, reviewProp))
                } else if (ObjCheck.isNullOrUndefined(reviewFieldVal)) {
                    diffs.push(diffFactory.create(label, "ALL", "Presented (val:" + editFieldVal + ")", "No Data",
                        Diff.ERROR, editProp, undefined))
                } else {
                    if (editFieldVal !== reviewFieldVal) {
                        diffs.push(diffFactory.create(label, "value", editFieldVal, reviewFieldVal,
                            Diff.ERROR, editProp, reviewProp))
                    }

                    if (ignoreNonCoreData !== true) {
                        diffs.push(...MetadataDiffExtractor.extract(label, editReport, reviewReport, editProp, reviewProp, ignoreExtendedSearchEnablement, diffFactory))
                    }
                }
            }
        }

        return diffs
    }

    static _getGetName(report) {
        return report[ReportField.YEAR] + "/" + report[ReportField.PERIOD] +
            "/" + report[ReportField.TYPE] + "/" + report[ReportField.REPORT_TYPE]
    }
}

class MetadataDiffExtractor {
    /**
     * When ignoreExtendedSearchEnablement is true this ignores extended search when the old value is false and the new value is true
     */
    static extract(label, editReport, reviewReport, editProp, reviewProp, ignoreExtendedSearchEnablement,
        diffFactory, areFieldValuesEmpty = false) {
        const diffs = []
        const editMetadata = ReportUtils.getFieldMetadataOrDefault(editReport, editProp)
        const reviewMetadata = ReportUtils.getFieldMetadataOrDefault(reviewReport, reviewProp)

        if (!DESCRIPTOR_IDS_TO_IGNORE_METADATA_REVIEW_FOR.includes(editProp)) {
            for (const metadataProp of METADATA_PROPS_FOR_REVIEW) {
                let editData = normalizeValue(editMetadata?.[metadataProp])
                let reviewData = normalizeValue(reviewMetadata?.[metadataProp])

                if(metadataProp === MetadataField.SRC) {
                    if((ObjCheck.isNullUndefinedEmptyOrDash(editData) || ObjCheck.isNullUndefinedEmptyOrDash(reviewData))
                        && areFieldValuesEmpty) {
                        continue;
                    }

                    // When neither side has an explicit field src, both are implicitly the report's
                    // src; any difference is then purely the report-level src changing, which is
                    // already reviewed as the report's own src field. Skip on the raw values to
                    // avoid emitting a redundant per-field src diff for every populated field.
                    if([editData, reviewData].every(i => FIELD_SOURCE_REPORT_EQUIVALENT_VALUES.includes(i))) {
                        continue;
                    }

                    // A field's src is only persisted explicitly when the report src is MIXED;
                    // otherwise it stays undefined and is implicitly the report's src. Compare the
                    // effective src on each side so an unset field src isn't flagged against an
                    // explicit one that resolves to the same source (avoids false positives).
                    editData = ReportUtils.getEffectiveSrc(editReport[ReportField.SRC], editData)
                    reviewData = ReportUtils.getEffectiveSrc(reviewReport[ReportField.SRC], reviewData)
                }

                if (BOOLEAN_METADATA_PROPS.includes(metadataProp)) {
                    editData = !!editData;
                    reviewData = !!reviewData
                }

                if ( ignoreExtendedSearchEnablement && metadataProp === MetadataField.EXTENDED_SEARCH &&
                    editData === true && reviewData === false ) {
                    continue;
                }

                if (editData !== reviewData) {
                    if (metadataProp === MetadataField.REPORTED_AS_LABEL &&
                        ObjCheck.isNullUndefinedEmptyOrDash(editData) &&
                        ObjCheck.isNullUndefinedEmptyOrDash(reviewData)) {
                        console.log("Skipping empty labels diff")
                    } else {
                        // Metadata is addressed by the same descriptor as the value it describes: a
                        // reported-as label or src was read from the same region of the same document.
                        diffs.push(diffFactory.create(label, metadataProp,
                            editData, reviewData, Diff.ERROR, editProp, reviewProp))
                    }
                }
            }
        }

        return diffs
    }
}
class DiffFactory {

    /**
     * 
     * Creates an instance of DiffFactory.
     * @param {Object} params - The parameters for the report context.
     * @param {number} params.year - The year of the report.
     * @param {string} params.period - The period of the report.
     * @param {string} params.version - The version of the report.
     * @param {string} params.format - The format of the report.
     * @param {number|null} [params.editReportId] - Id of the edit-side report, for provenance lookups.
     * @param {number|null} [params.reviewReportId] - Id of the review-side report, for provenance lookups.
     */
    constructor({ year, period, version, format, editReportId = null, reviewReportId = null }) {
        this.year = year
        this.period = period
        this.version = version
        this.format = format
        this.editReportId = editReportId
        this.reviewReportId = reviewReportId
        this.name = year + "/" + period +
            "/" + version + "/" + format
    }

    /**
     * Creates a new Diff instance with the provided parameters.
     *
     * @param {string} fieldLabel - The label of the field being compared.
     * @param {string} property - The property name being compared.
     * @param {*} edit
     * @param {*} review
     * @param {string} [type=Diff.ERROR] - The type of difference (default is Diff.ERROR).
     * @param {string} [editDescriptorId] - Descriptor id on the edit side. Together with the
     * factory's editReportId it addresses the field's provenance. Left unset when that side has
     * nothing to trace — a missing descriptor, or "No Data".
     * @param {string} [reviewDescriptorId] - Descriptor id on the review side. Same rules.
     * @returns {Diff} A new Diff instance representing the difference.
     */
    create(fieldLabel, property, edit, review, type = Diff.ERROR, editDescriptorId, reviewDescriptorId) {
        return new Diff(this.name, fieldLabel, property, edit, review,
            this.year, this.period, this.version, this.format, type,
            this.editReportId, editDescriptorId, this.reviewReportId, reviewDescriptorId)
    }
}


class Diff {
    static ERROR = "ERROR"
    static WARNING = "WARNING"

    /**
     * @param {string} name Format: {YEAR}/{PERIOD}/{VERSION}/{FORMAT} Required
     * @param {string} fieldLabel - The label of the field being compared.
     * @param {string} property - The property name being compared.
     * @param {*} edit
     * @param {*} review
     * @param {number} year - The year of the report.
     * @param {string} period - The period of the report.
     * @param {string} version - The version of the report.
     * @param {string} format - The format of the report.
     * @param {string} [type=Diff.ERROR] - The type of difference (default is Diff.ERROR).
     * @param {number|null} [editReportId] - Addresses the edit value's provenance, with editDescriptorId.
     * @param {string} [editDescriptorId] - Addresses the edit value's provenance, with editReportId.
     * @param {number|null} [reviewReportId] - Addresses the review value's provenance.
     * @param {string} [reviewDescriptorId] - Addresses the review value's provenance.
     */
    constructor(name, fieldLabel, property, edit, review, year, period, version, format, type = Diff.ERROR,
        editReportId, editDescriptorId, reviewReportId, reviewDescriptorId) {
        this.name = name
        this.fieldLabel = fieldLabel
        this.property = property
        this.edit = edit
        this.review = review
        this.type = type
        this.editReportId = editReportId
        this.editDescriptorId = editDescriptorId
        this.reviewReportId = reviewReportId
        this.reviewDescriptorId = reviewDescriptorId
        this.reportInfo = {
            year,
            period,
            [ReportField.TYPE]: version,
            [ReportField.REPORT_TYPE]: format,
        }
    }
}

class ReportPair {

    /**
     * @param editReport  Optional 
     * @param reviewReport Optional 
     */
    constructor(editReport, reviewReport) {
        this.editReport = editReport
        this.reviewReport = reviewReport
    }
}

export { ReportDiffUtil, Diff } 